var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import querystring from 'querystring';
import { useCallback, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import sleep from '../utils/sleep';
import getFetch from '../LinkApiRouter/utils/isofetch';
import eventQueue from '../events/queue';
var DEFAULT_TIMEOUT_S = 60 * 1000;
var addQueryToUrl = function (url, queryParams) {
    return !queryParams ? url : url.split('?')[0] + querystring.stringify(queryParams);
};
var IDENTITY = function (x) { return x; };
/**
 * Checks for if we should retry
 *
 * @param {any} error - from request in catch block
 * @param {number} tryCount - number of attempts so far
 * @param {number} initialRequestTime - timestamp of initial request
 * @param {FetchOptionsRetryConfig} retryConfig
 *
 * If no retryConfig is present, return false.
 * If checkForIsRetryable is present and returns false, return false.
 * If has remaining retries or remaining time, return true.
 */
function checkShouldRetry(error, tryCount, initialRequestTime, retryConfig) {
    var hasRetryConfig = retryConfig && Object.keys(retryConfig).length;
    if (!hasRetryConfig)
        return false;
    var maxTimeS = retryConfig.maxTimeS, maxTries = retryConfig.maxTries, checkForIsRetryable = retryConfig.checkForIsRetryable;
    var isExplicitlyRetryable = !checkForIsRetryable || checkForIsRetryable(error);
    if (!isExplicitlyRetryable)
        return false;
    var hasMoreRetries = tryCount < maxTries;
    var hasMoreTime = Date.now() - maxTimeS * 1000 < initialRequestTime;
    return hasMoreRetries || hasMoreTime;
}
/**
 * Hook that returns fetch function and associated data
 * @param {string} url - url to fetch
 * @param {FetchRequest<ReqBody>} request - request body
 * @param {FetchOptions<ReqBody, Res>} options - request options (callbacks, timeout, retry config, etc)
 */
export default function useLazyFetch(url, request, options) {
    var _this = this;
    if (options === void 0) { options = { retryConfig: {} }; }
    var _a = useState(false), loading = _a[0], setLoading = _a[1];
    var _b = useState(), error = _b[0], setError = _b[1];
    var _c = useState(), data = _c[0], setData = _c[1];
    var _d = useState(0), tryCount = _d[0], setTryCount = _d[1];
    var _e = useState(0), initialRequestTime = _e[0], setInitialRequestTime = _e[1];
    var makeRequest = useCallback(function (makeRequestOptions) {
        if (makeRequestOptions === void 0) { makeRequestOptions = {}; }
        var bodyOverride = makeRequestOptions.bodyOverride, queryParams = makeRequestOptions.queryParams, urlOverride = makeRequestOptions.urlOverride, methodOverride = makeRequestOptions.methodOverride;
        if (!initialRequestTime)
            setInitialRequestTime(Date.now());
        setTryCount(tryCount + 1);
        setLoading(true);
        var controller = new AbortController();
        var fetchOpts = {
            signal: controller.signal,
        };
        var timeoutId = setTimeout(function () { return controller.abort(); }, options.timeout || DEFAULT_TIMEOUT_S);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var body = request.body, reqWithoutBody = __rest(request, ["body"]);
        Object.assign(fetchOpts, reqWithoutBody);
        // Avoid "Failed to execute 'fetch' on 'Window': Request with GET/HEAD method cannot have body"
        // by checking the method.
        if (fetchOpts.method !== 'GET' && (request.body || bodyOverride))
            Object.assign(fetchOpts, {
                body: JSON.stringify((options.formatRequest || IDENTITY)(
                // Override the entire body if `bodyOverride`
                bodyOverride || request.body)),
            });
        if (methodOverride)
            Object.assign(fetchOpts, { method: methodOverride });
        // If initial method was POST with a body, and subsequent method is GET, we need to delete
        // the request body to avoid "Request with GET/HEAD method cannot have body" error.
        if (methodOverride === 'GET')
            delete fetchOpts.body;
        var params = [
            addQueryToUrl(urlOverride || url, queryParams),
            fetchOpts,
        ];
        if (options.onRequest) {
            options.onRequest.apply(options, params);
        }
        if (options.debug) {
            // eslint-disable-next-line no-console
            console.log('DEBUG REQUEST INFO', {
                url: params[0],
                fetchOpts: fetchOpts,
                request: request,
                options: options,
                makeRequestOptions: makeRequestOptions,
            });
        }
        // Note: `finally` is not supported in older browsers, so we want to
        // set `promise` and then use `then` instead of `finally`.
        var promise = getFetch().apply(void 0, params).then(function (result) { return __awaiter(_this, void 0, void 0, function () {
            var rawResult, json;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, result.json()];
                    case 1:
                        rawResult = _a.sent();
                        json = (options.formatResponse || IDENTITY)(rawResult);
                        setData(json);
                        if (options.onResult)
                            options.onResult(json);
                        if (options.debug) {
                            // eslint-disable-next-line no-console
                            console.log('DEBUG RESPONSE INFO', {
                                params: params,
                                json: json,
                            });
                        }
                        return [2 /*return*/];
                }
            });
        }); })
            .catch(function (e) {
            // (11.09.22) Log any unknown errors for now to see if we ever actually get here and
            // what the error is if we do.
            // Tracking here: https://app.datadoghq.com/logs?query=%22Caught%20unknown%20request%20error%20in%20Link%22
            datadogLogs.logger.log('Caught unknown request error in Link', {
                unknownError: e,
            });
            eventQueue.emit('error', { error: e });
            setError(e);
            if (options.onError)
                options.onError({ error: e, makeRequest: makeRequest });
            if (options.debug) {
                // eslint-disable-next-line no-console
                console.log('DEBUG ERROR INFO', {
                    params: params,
                    error: e,
                });
            }
            var retryConfig = options.retryConfig;
            var shouldRetry = checkShouldRetry(error, tryCount, initialRequestTime, retryConfig);
            if (shouldRetry)
                void sleep(retryConfig.retryWaitS || 1).then(function () { return makeRequest(); });
        });
        void promise.then(function () {
            setLoading(false);
            clearTimeout(timeoutId);
            if (options.onFinal)
                options.onFinal();
        });
    }, [error, initialRequestTime, options, request, tryCount, url]);
    return {
        data: data,
        error: error,
        loading: loading,
        makeRequest: makeRequest,
    };
}
